import React from 'react';
import { Badge, Button, Image, Table } from 'react-bootstrap';
import './index.css';
import { ToWords } from 'to-words';
import { format } from 'date-fns';

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatINR = (n) =>
  safeNum(n).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const InvoiceTemplate = ({
  totalPaidAmount,          // kept for compatibility, but NOT used for balance now
  targetInvoiceRef,
  navigate,
  state,
  invoiceDetails,
  businessProfile,
  overdueSummary,          // optional, not used here directly
}) => {
  const toWords = new ToWords();

  // ---------- Base fields ----------
  const principal = safeNum(invoiceDetails?.principal);
  const tax = safeNum(invoiceDetails?.tax);

  const overdueInterest = safeNum(invoiceDetails?.overdueInterest);
  const overdueTax = safeNum(invoiceDetails?.overdueTax);
  const overdueTotal = overdueInterest + overdueTax;

  // New: received breakdown from invoice document
  const principalReceived = safeNum(invoiceDetails?.principalReceived);
  const taxReceived = safeNum(invoiceDetails?.taxReceived);
  const overdueInterestReceived = safeNum(
    invoiceDetails?.overdueInterestReceived
  );
  const overdueTaxReceived = safeNum(
    invoiceDetails?.overdueTaxReceived
  );

  // ---------- Totals ----------
  const baseTotal = principal + tax;
  const baseReceived = principalReceived + taxReceived;
  const baseOutstanding = Math.max(0, baseTotal - baseReceived);

  const overdueReceived = overdueInterestReceived + overdueTaxReceived;
  const overdueOutstanding = Math.max(0, overdueTotal - overdueReceived);

  const totalAmount = baseTotal + overdueTotal;
  const totalReceived =
    principalReceived +
    taxReceived +
    overdueInterestReceived +
    overdueTaxReceived;
  const grandOutstanding = Math.max(0, totalAmount - totalReceived);

  // IMPORTANT CHANGE:
  // "Balance Amount" will show ONLY base (principal+tax) outstanding,
  // not including overdue.
  const balanceValue = baseOutstanding;

  const amountInWords = toWords.convert(totalAmount || 0, {
    currency: true,
  });

  // ---------- Tax split (SGST/CGST vs IGST) ----------
  const taxRate = safeNum(state?.taxRate);
  const isInterState =
    state?.partyDetails?.state &&
    state?.projectDetails?.propState &&
    state.partyDetails.state !== state.projectDetails.propState;

  const igstValue = isInterState ? tax : 0;
  const sgstValue = !isInterState ? tax / 2 : 0;
  const cgstValue = !isInterState ? tax / 2 : 0;

  // ---------- Status badge based on TOTAL outstanding ----------
  const outstandingEpsilon = 0.01;

  let badgeText = 'Outstanding';
  let badgeVariant = 'danger';

  if (totalAmount <= outstandingEpsilon) {
    badgeText = 'Outstanding';
    badgeVariant = 'danger';
  } else if (grandOutstanding <= outstandingEpsilon) {
    badgeText = 'Paid';
    badgeVariant = 'success';
  } else if (totalReceived === 0) {
    badgeText = 'Outstanding';
    badgeVariant = 'danger';
  } else if (totalReceived > 0 && totalReceived < totalAmount) {
    badgeText = 'Partially Paid';
    badgeVariant = 'info';
  }

  // ---------- Dates ----------
  const bookingDate = state?.bookingDate
    ? new Date(state.bookingDate.seconds * 1000)
    : null;
  const dueDate = invoiceDetails?.dueDate
    ? invoiceDetails.dueDate.toDate()
    : null;
  const createdOn = invoiceDetails?.createdOn
    ? invoiceDetails.createdOn.toDate()
    : null;

  return (
    <div className='border rounded'>
      <div ref={targetInvoiceRef}>
        {/* HEADER */}
        <div className='row p-5 pb-3'>
          <div className='col-6'>
            {businessProfile?.companyLogo ? (
              <Image
                src={businessProfile.companyLogo}
                className='invoice-logo'
                rounded
              />
            ) : (
              <Button
                variant='link'
                className='p-0 rounded text-decoration-none'
                onClick={() => navigate('/admin/settings/company')}
              >
                <div className='invoice-logo-placeholder'>
                  Logo
                </div>
              </Button>
            )}
          </div>
          <div className='col-6 d-flex justify-content-end'>
            <div className='d-block text-end'>
              <Badge
                bg={`${badgeVariant} border-0 px-3 py-1 mb-3`}
              >
                <h6 className='mb-0 fw-light'>
                  <small>{badgeText}</small>
                </h6>
              </Badge>
              <h3>{invoiceDetails && invoiceDetails.id}</h3>
            </div>
          </div>
        </div>

        {/* TO / FROM */}
        <div className='row px-5'>
          <div className='col-6 d-flex justify-content-start'>
            <div className='text-start'>
              <h6 className='fw-normal text-muted text-uppercase'>
                <small>Invoice to</small>
              </h6>
              <p className='mb-0 fw-600'>
                {state?.partyDetails?.partyName}
              </p>
              <p className='mb-0'>
                {state?.partyDetails?.address && (
                  <>{state.partyDetails.address},</>
                )}
              </p>
              <p className='mb-0'>
                {state?.partyDetails?.city && (
                  <>{state.partyDetails.city}, </>
                )}
                {state?.partyDetails?.state && (
                  <>{state.partyDetails.state}, </>
                )}
                {state?.partyDetails?.pincode}
              </p>
              <p className='mb-0'>
                +91 {state?.partyDetails?.phoneNumber}
              </p>
            </div>
          </div>
          <div className='col-6 d-flex justify-content-end'>
            <div className='text-end'>
              <h6 className='fw-normal text-muted text-uppercase'>
                <small>Invoice from</small>
              </h6>
              <p className='mb-0 fw-600'>
                {businessProfile?.businessName}
              </p>
              <p className='mb-0'>
                {businessProfile?.address && (
                  <>{businessProfile.address},</>
                )}
              </p>
              <p className='mb-0'>
                {businessProfile?.city && (
                  <>{businessProfile.city}, </>
                )}
                {businessProfile?.state && (
                  <>{businessProfile.state}, </>
                )}
                {businessProfile?.pincode}
              </p>
              <p className='mb-0'>
                +91 {businessProfile?.phoneNumber}
              </p>
            </div>
          </div>
        </div>

        <hr />

        {/* DATES */}
        <div className='row px-5'>
          <div className='col-4 d-flex justify-content-start'>
            <div className='text-start'>
              <h6 className='fw-normal text-muted mb-0'>
                <small>Booking Date</small>
              </h6>
              <p className='mb-0'>
                {bookingDate
                  ? format(bookingDate, 'dd-MM-yyyy')
                  : '-'}
              </p>
            </div>
          </div>
          <div className='col-4 d-flex justify-content-center'>
            <div className='text-center'>
              <h6 className='fw-normal text-muted mb-0'>
                <small>Due Date</small>
              </h6>
              <p className='mb-0'>
                {dueDate ? format(dueDate, 'dd-MM-yyyy') : '-'}
              </p>
            </div>
          </div>
          <div className='col-4 d-flex justify-content-end'>
            <div className='text-end'>
              <h6 className='fw-normal text-muted mb-0'>
                <small>Invoice Date</small>
              </h6>
              <p className='mb-0'>
                {createdOn
                  ? format(createdOn, 'dd-MM-yyyy')
                  : '-'}
              </p>
            </div>
          </div>
        </div>

        <hr />

        {/* PROPERTY INFO */}
        <div className='row mt-0 px-5'>
          <div className='col-md-9'>
            <div className='text-muted fw-normal'>
              <small>Property Description</small>
            </div>
            <div className='fw-600'>
              {state?.projectDetails?.propName}
            </div>
            <div>
              {state?.projectDetails?.propNumber && (
                <>{state.projectDetails.propNumber}, </>
              )}
              {state?.projectDetails?.propCity && (
                <>{state.projectDetails.propCity}, </>
              )}
              {state?.projectDetails?.propState && (
                <>{state.projectDetails.propState}, </>
              )}
              {state?.projectDetails?.propPincode}
            </div>
          </div>
          <div className='col-md-3 text-end'>
            <div className='text-muted fw-normal'>
              <small>Unit</small>
            </div>
            <div className='fw-normal'>
              {state?.projectDetails?.bookedUnit?.unitName}{' '}
              (
              {state?.projectDetails?.bookedUnit?.unitArea &&
                `${state.projectDetails.bookedUnit.unitArea} ${state.projectDetails.propUnitMeasure || ''
                }`}
              )
            </div>
            <div className='mt-2'>
              <small>
                <span className='me-1 border rounded px-2 py-1 bg-primary-light'>
                  Floor -{' '}
                  <span>
                    {
                      state?.projectDetails?.bookedUnit
                        ?.unitFloor
                    }
                  </span>
                </span>
                <span className='ms-1 border rounded px-2 py-1 bg-primary-light'>
                  Unit No. -{' '}
                  <span>
                    {
                      state?.projectDetails?.bookedUnit
                        ?.unitNumber
                    }
                  </span>
                </span>
              </small>
            </div>
          </div>
        </div>

        {/* LINE ITEMS */}
        <div className='mt-4 py-3 px-5 bg-primary-light'>
          <Table bordered className='mb-0'>
            <thead>
              <tr className='text-center border-bottom'>
                <th className='fw-normal text-muted text-start py-2 px-3'>
                  <small>Particular</small>
                </th>
                <th className='fw-normal text-muted py-2 px-3'>
                  <small>Taxable</small>
                </th>
                <th className='fw-normal text-muted py-2 px-3'>
                  <small>Tax ({taxRate}%)</small>
                </th>
                <th className='fw-normal text-muted py-2 px-3'>
                  <small>Overdue</small>
                </th>
                <th className='fw-normal text-muted text-end py-2 px-3'>
                  <small>Amount</small>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className='text-center'>
                <td className='text-start py-2 px-3'>
                  <p className='mb-1'>
                    {invoiceDetails?.name}
                  </p>
                  <p className='mb-0 fw-light text-muted lh-1'>
                    <small>
                      {invoiceDetails?.description}
                    </small>
                  </p>
                </td>
                <td className='py-2 px-3'>
                  {formatINR(principal)}
                </td>
                <td className='py-2 px-3'>
                  {formatINR(tax)}
                </td>
                <td className='py-2 px-3 text-danger'>
                  {formatINR(overdueTotal)}
                </td>
                <td className='text-end py-2 px-3'>
                  {formatINR(totalAmount)}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* FOOTER: TERMS, TAX SPLIT, TOTALS */}
        <div className='row mt-3 px-5'>
          <div className='col-6'>
            {state?.termsConditions && (
              <div className='mb-3 me-5'>
                <h6 className='fw-normal mb-0 text-muted'>
                  <small>Terms and Conditions</small>
                </h6>
                <p className='text-justify'>
                  <small>{state.termsConditions}</small>
                </p>
              </div>
            )}
            {state?.note && (
              <div className='mb-3 me-5'>
                <h6 className='fw-normal mb-0 text-muted'>
                  <small>Notes</small>
                </h6>
                <p className='text-justify'>
                  <small>{state.note}</small>
                </p>
              </div>
            )}
          </div>
          <div className='col-6'>
            <div className='d-flex align-items-center justify-content-between'>
              <div className='fw-600'>Taxable Amount</div>
              <div className='fw-bold text-end'>
                {formatINR(principal)}
              </div>
            </div>

            {isInterState ? (
              <div className='mt-2'>
                <div className='row'>
                  <div className='col-8'>
                    IGST @ {taxRate}%
                  </div>
                  <div className='col-4 text-end'>
                    {formatINR(igstValue)}
                  </div>
                </div>
              </div>
            ) : (
              <div className='mt-2'>
                <div className='row'>
                  <div className='col-8'>
                    SGST @ {taxRate / 2}%
                  </div>
                  <div className='col-4 text-end'>
                    {formatINR(sgstValue)}
                  </div>
                </div>
                <div className='row'>
                  <div className='col-8'>
                    CGST @ {taxRate / 2}%
                  </div>
                  <div className='col-4 text-end'>
                    {formatINR(cgstValue)}
                  </div>
                </div>
              </div>
            )}

            {overdueTotal > 0 && (
              <div className='d-flex align-items-center justify-content-between text-danger mt-1'>
                <div className='fw-normal'>
                  Overdue Charges (incl. tax)
                </div>
                <div className='fw-normal text-end'>
                  {formatINR(overdueTotal)}
                </div>
              </div>
            )}

            <hr />
            <div className='d-flex align-items-center justify-content-between mt-2'>
              <h5 className='fw-600 mb-0'>Total Amount</h5>
              <h5 className='fw-600 mb-0 text-end'>
                {formatINR(totalAmount)}
              </h5>
            </div>
            <hr />
            <div className='mt-0 d-flex align-items-center justify-content-end'>
              <small>
                <span className='text-end'>{amountInWords}</span>
              </small>
            </div>
            {/* NEW: show balance = base outstanding only */}
            {
              balanceValue === 0 ?
                null
                :
                <>
                  <hr />
                  <div className='d-flex align-items-center justify-content-between text-danger mt-1'>
                    <div className='fw-normal'>
                      Balance Amount
                    </div>
                    <div className='fw-normal text-end'>
                      {formatINR(balanceValue)}
                    </div>
                  </div>
                </>
            }

            {/* Optional: show overdue outstanding separately */}
            {overdueOutstanding > 0 && (
              <div className='d-flex align-items-center justify-content-between text-danger mt-1'>
                <div className='fw-normal'>
                  Overdue Outstanding
                </div>
                <div className='fw-normal text-end'>
                  {formatINR(overdueOutstanding)}
                </div>
              </div>
            )}

            <div className='mt-5 pt-5 d-flex align-items-end justify-content-end'>
              <div className='mt-5 pb-5'>
                Authorized Signature
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceTemplate;